import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { move } from "@dnd-kit/helpers";
import { useMutation } from "@tanstack/react-query";
import {
  BuildingIcon,
  CheckIcon,
  HomeIcon,
  LightbulbIcon,
  LinkIcon,
  PlusIcon,
  Redo2Icon,
  SettingsIcon,
  ShuffleIcon,
  PencilIcon,
  TargetIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { languages, setInterpolationDictionary } from "simplydo/core/i18n";
import { getImageUrl } from "simplydo/core/images";
import { asMutation } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { css, cx, styled } from "simplydo/react/styled";
import { loadGoogleFont } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input, type InputProps } from "simplydo/react/ui/input";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { TextArea } from "simplydo/react/ui/textarea";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import Prompt from "utils/Prompt";
import constants from "utils/constants";
import util from "utils/utils";

import { ColourChooser, FileChooser } from "components/lib/Choosers";
import { DndContext } from "components/lib/dnd/DndContext";
import { Sortable } from "components/lib/dnd/Sortable";
import { makeResult } from "components/lib/dnd/dndHelpers";

import type {
  OnOrganisationEditedFn,
  OrgNavBarItem,
  OrgNavLink,
  OrganisationSettingsUpdate,
  OrganisationTerminology,
  SettingsOwnerOrganisation,
} from "../types";
import { SegmentContainer } from "./General";

const capitalisedInputClass = css`
  input {
    text-transform: capitalize;
  }
`;

const CapitalisedInput = ({ className, ...props }: InputProps) => (
  <Input className={cx(capitalisedInputClass, className)} {...props} />
);

type OrgNavBar = OrgNavBarItem[];

const emptyOrgNavLink = (): OrgNavLink => ({
  id: "",
  title: "",
  location: "",
  type: "link",
});

const defaultOrgNavBarItems: OrgNavBar = [
  { id: "home", translate: "generic.home", location: "/", icon: HomeIcon },
  {
    id: "challenges",
    translate: "generic.challenges",
    location: "/challenges",
    icon: TargetIcon,
  },
  {
    id: "ideas",
    translate: "generic.ideas",
    location: "/ideas",
    icon: LightbulbIcon,
  },
  {
    id: "groups",
    translate: "generic.groups",
    location: "/groups",
    icon: UsersIcon,
  },
  {
    id: "admin",
    translate: "generic.admin",
    location: "/admin",
    permissions: ["org.viewDashboard"],
    icon: SettingsIcon,
  },
  {
    id: "innointel",
    translate: "innointel.businessSearchTitle",
    location: "/innointel",
    permissions: ["org.innovationIntelligence"],
    icon: BuildingIcon,
  },
];

export const useOrgNavBar = () => {
  const user = useAppSelector((state) => state.user);
  const orgHasCustomNavBar = !!user?.ownerOrganisation?.navbar?.length;
  const orgId = user?.ownerOrganisation?._id;

  const innointelEnabled = useMemo(
    () =>
      user?.ownerOrganisation?.enabledFeatures?.includes(
        "innovationIntelligence",
      ),
    [user],
  );
  const localSearchEnabled = useMemo(
    () =>
      innointelEnabled &&
      user?.ownerOrganisation?.enabledFeatures?.includes(
        "innovationIntelligenceLocalSearch",
      ),
    [user, innointelEnabled],
  );

  const visibleDefaultItems = useMemo(
    () =>
      defaultOrgNavBarItems.filter((item) => {
        if (item.id === "innointel" && !innointelEnabled) {
          return false;
        }
        if (item.id === "innointel" && !localSearchEnabled) {
          return false;
        }
        return true;
      }),
    [innointelEnabled, localSearchEnabled],
  );

  const allItems: OrgNavBarItem[] = useMemo(
    () =>
      visibleDefaultItems
        .filter((item) => {
          if (item.id === "innointel" && !innointelEnabled) {
            return false;
          }
          if (item.id === "innointel" && !localSearchEnabled) {
            return false;
          }
          return true;
        })
        .concat(
          (user?.ownerOrganisation?.links || []).map((link) => ({
            ...link,
            type: "link",
            icon: LinkIcon,
          })) || [],
        ),
    [
      visibleDefaultItems,
      user?.ownerOrganisation?.links,
      innointelEnabled,
      localSearchEnabled,
    ],
  );

  const entries: OrgNavBarItem[] = useMemo(() => {
    const navbarItems: OrgNavBarItem[] = orgHasCustomNavBar
      ? user?.ownerOrganisation?.navbar?.map((item) => {
          if (item.type === "link") {
            const fullLink = allItems.find((link) => link.id === item.id);
            if (fullLink) {
              return fullLink;
            }
          }
          const defaultItem = visibleDefaultItems.find(
            (defaultEntry) => defaultEntry.id === item.id,
          );
          if (defaultItem) {
            return defaultItem;
          }
          // fallback for custom links that have been removed from org settings but are still in navbar
          return item;
        }) || []
      : visibleDefaultItems;

    return navbarItems.filter((entry) => {
      if (!entry.permissions) {
        return true;
      }
      return !!entry.permissions.find((permission) =>
        util.hasPermission(user, permission, orgId),
      );
    });
  }, [visibleDefaultItems, user, allItems, orgHasCustomNavBar, orgId]);

  return { entries, allItems };
};

const flatOrgPermissions = constants.permissions.org.flatMap(
  (category) => category.permissions,
);

const NavBarEntry = ({
  item,
  setEditingLinkModalOpen,
  setEditingLink,
  deleteCustomLink,
}: {
  item: OrgNavBarItem;
  setEditingLinkModalOpen: (open: boolean) => void;
  setEditingLink: (
    link: NonNullable<OpenAPI.Schemas["Organisation"]["links"]>[number],
  ) => void;
  deleteCustomLink: (linkId: string) => void;
}) => {
  const { t } = useTranslation();
  const permissions = (item.permissions || [])
    .map((permission) =>
      t(
        flatOrgPermissions.find((p) => p.key === permission)?.title ??
          permission,
      ),
    )
    .join(", ");
  return (
    <Card fluid elevation="small">
      <CardContent
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0.6em 1em",
          minHeight: "45px",
          gap: 4,
        }}
      >
        <Icon icon={item.icon} style={{ marginLeft: 6, marginRight: 12 }} />
        <div style={{ flex: 1 }}>
          {item.translate
            ? t("common:capitalise", { key: item.translate })
            : item.title}
          <p style={{ fontSize: "0.7em", textTransform: "none" }}>
            {item.type === "page"
              ? "Custom page"
              : item.type === "link"
                ? "Custom link:  "
                : "Default entry"}
            {permissions
              ? `. Visibility restricted to users with permissions: ${permissions}`
              : ""}
            {item.type === "link" && `${item.location}`}
          </p>
        </div>
        {item.type === "link" && (
          <>
            <Button size="small" onClick={() => deleteCustomLink(item.id)}>
              <Icon icon={Trash2Icon} />
            </Button>
            <Button
              size="small"
              onClick={() => {
                setEditingLinkModalOpen(true);
                // @ts-ignore
                setEditingLink(item);
              }}
            >
              <Icon icon={PencilIcon} />
              Edit
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const defaultFontFamily = {
  h1: "Inter",
  h2: "Inter",
  h3: "Inter",
  h4: "Inter",
  h5: "Inter",
  body: "Inter",
};

const SettingsBranding = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const orgId = user?.ownerOrganisation?._id;
  const organisation = user?.ownerOrganisation as
    | SettingsOwnerOrganisation
    | undefined;

  const { entries, allItems } = useOrgNavBar();

  const [visibleEntries, setVisibleEntries] = useState(entries);
  const [hiddenEntries, setHiddenEntries] = useState<OrgNavBar>([]);
  const [navBarUnsaved, setNavBarUnsaved] = useState(false);
  const [fontFamily, setFontFamily] = useState(
    user?.ownerOrganisation?.fontFamily ?? defaultFontFamily,
  );

  useEffect(() => {
    setVisibleEntries(entries);
  }, [entries]);

  useEffect(() => {
    setHiddenEntries(
      allItems.filter(
        (item) =>
          !visibleEntries.find((enabledItem) => enabledItem.id === item.id),
      ),
    );
  }, [visibleEntries, allItems]);

  const [newTitle, setNewTitle] = useState(user?.ownerOrganisation?.appName);
  const [newWelcomeText, setNewWelcomeText] = useState(
    user?.ownerOrganisation?.welcomeText,
  );
  const [newDashboardText, setNewDashboardText] = useState(
    user?.ownerOrganisation?.dashboardText,
  );
  const defaultHomepageTasklist = {
    completeProfile: true,
    exploreChallenges: true,
    createIdea: true,
    submitIdea: false,
    joinGroup: false,
    setup2fa: false,
  };
  const [homepageTasklist, setHomepageTasklist] = useState({
    ...defaultHomepageTasklist,
    ...user?.ownerOrganisation?.homepageTasklist,
  });
  const [editingLinkModalOpen, setEditingLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<OrgNavLink>(emptyOrgNavLink());

  const [newTerminology, setNewTerminology] = useState<OrganisationTerminology>(
    organisation?.terminology ?? {},
  );
  const [terminologyTranslationOpen, setTerminologyTranslationOpen] = useState<{
    challenge?: boolean;
    idea?: boolean;
    impact?: boolean;
    group?: boolean;
  }>({});

  const onOrganisationEdited = useCallback<OnOrganisationEditedFn>(
    (field, value) => {
      dispatch(actions.user.updateOrganisation(field, value));
    },
    [dispatch],
  );

  const updateOrgMutation = useMutation({
    mutationFn: asMutation(api.organisations.updateSettings)!,
    onSuccess: (data) => {
      if (!orgId) return;
      const { navbar, links } = data as SettingsOwnerOrganisation;
      api.setQueryData(api.organisations.get, [orgId], (prev) => ({
        ...prev,
        navbar,
        links,
      }));
      onOrganisationEdited("links", links);
      onOrganisationEdited("navbar", navbar);
    },
  });

  const toDatabase = useCallback(
    (entries: OrgNavBar) =>
      entries.map((item) =>
        item.type ? { id: item.id, type: item.type } : { id: item.id },
      ),
    [],
  );

  const updateOrgLinks = useCallback(() => {
    if (!orgId || !organisation) return;
    const orgLinks = organisation.links || [];
    const existingLink = orgLinks.find((link) => link.id === editingLink?.id);
    const updatedLinks = [...orgLinks];
    const updatedNavbar = [...visibleEntries];

    if (existingLink) {
      const updatedLinksIndex = orgLinks.findIndex(
        (link) => link.id === existingLink.id,
      );
      updatedLinks[updatedLinksIndex] = {
        ...existingLink,
        title: editingLink.title,
        location: editingLink.location,
        external: editingLink.external,
      };

      const navbarIndex = visibleEntries.findIndex(
        (link) => link.id === existingLink.id,
      );
      if (navbarIndex !== -1) {
        const currentItem = updatedNavbar[navbarIndex];
        if (!currentItem) return;
        updatedNavbar[navbarIndex] = {
          ...currentItem,
          id: currentItem.id,
          title: editingLink.title,
          location: editingLink.location,
          external: editingLink.external,
        };
        setVisibleEntries(updatedNavbar);
      }
    } else {
      updatedLinks.push({ ...editingLink });
    }

    updateOrgMutation.mutate([
      orgId,
      {
        links: updatedLinks,
        navbar: toDatabase(updatedNavbar),
      } as OrganisationSettingsUpdate,
    ]);
    setEditingLinkModalOpen(false);
  }, [
    editingLink,
    visibleEntries,
    organisation,
    orgId,
    updateOrgMutation,
    toDatabase,
  ]);

  const deleteCustomLink = useCallback(
    (linkId: string) => {
      if (!orgId || !organisation) return;
      const updatedLinks =
        organisation.links?.filter((link) => link.id !== linkId) ?? [];
      const updatedNavbar = visibleEntries.filter((link) => link.id !== linkId);

      setVisibleEntries(updatedNavbar);

      updateOrgMutation.mutate([
        orgId,
        {
          links: updatedLinks,
          navbar: toDatabase(updatedNavbar),
        } as OrganisationSettingsUpdate,
      ]);
    },
    [organisation, orgId, updateOrgMutation, visibleEntries, toDatabase],
  );

  const saveOrg = useCallback(
    (
      field: keyof SettingsOwnerOrganisation,
      value?: SettingsOwnerOrganisation[keyof SettingsOwnerOrganisation] | null,
      hideToast?: boolean,
    ) => {
      if (!organisation?._id) return;
      const currentValue = organisation[field];
      if (currentValue !== value) {
        onOrganisationEdited(field, value);
      }
      api.organisations.updateSettings(
        organisation._id,
        { [field]: value } as OrganisationSettingsUpdate,
        () => {
          if (!hideToast) {
            toast.success(t("generic.typeUpdated", { type: "Settings" }));
          }
        },
        (err) => {
          toast.error(err.message);
          onOrganisationEdited(field, currentValue);
        },
      );
    },
    [onOrganisationEdited, organisation, t],
  );

  const saveLogo = useCallback(
    (logo: string) => {
      if (!orgId) return;
      api.organisations.updateSettings(
        orgId,
        { darkLogo: logo },
        (organisation) => {
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
          onOrganisationEdited("darkLogo", logo);
          onOrganisationEdited("darkLogoUrl", organisation.darkLogoUrl);
          onOrganisationEdited("darkLogoThumbnails", {});
          onOrganisationEdited("darkLogoThumbnailUrls", {});
        },
        (err) => toast.error(err.message),
      );
    },
    [onOrganisationEdited, t, orgId],
  );

  const saveFavicon = useCallback(
    (favicon: string, _name?: string, faviconUrl?: string) => {
      if (!orgId) return;
      api.organisations.updateSettings(
        orgId,
        { favicon } as OrganisationSettingsUpdate,
        () => {
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
          onOrganisationEdited("favicon", favicon);
          onOrganisationEdited("faviconUrl", faviconUrl);
          onOrganisationEdited("faviconThumbnails", {});
          onOrganisationEdited("faviconThumbnailUrls", {});
        },
        (err) => toast.error(err.message),
      );
    },
    [onOrganisationEdited, t, orgId],
  );

  const swapColours = useCallback(() => {
    if (!organisation) return;
    const { primaryColour, secondaryColour } = organisation;
    saveOrg("primaryColour", secondaryColour, true);
    saveOrg("secondaryColour", primaryColour, true);
  }, [saveOrg, organisation]);

  const setColour = useCallback(
    (type: "primaryColour" | "secondaryColour", colour: string) => {
      saveOrg(type, colour, true);
    },
    [saveOrg],
  );

  if (!orgId || !organisation) {
    return null;
  }

  const changes: Partial<
    Pick<SettingsOwnerOrganisation, "appName" | "welcomeText" | "dashboardText">
  > = {};
  if (newTitle !== organisation.appName) {
    changes.appName = newTitle;
  }
  if (newWelcomeText !== organisation.welcomeText) {
    changes.welcomeText = newWelcomeText;
  }
  if (newDashboardText !== organisation.dashboardText) {
    changes.dashboardText = newDashboardText;
  }
  const changed = Object.keys(changes).length > 0;

  return (
    <div>
      <Prompt
        when={changed}
        message={"You have unsaved changes. Are you sure you want to leave?"}
      />
      {changed ? (
        <Message color="blue">
          <MessageHeader>You have unsaved changes.</MessageHeader>
          <p>Before leaving this page make sure to save your changes.</p>
          <Button
            size="small"
            onClick={() => {
              Object.entries(changes).forEach(([key, value], i, entries) => {
                saveOrg(
                  key as keyof SettingsOwnerOrganisation,
                  value,
                  i === entries.length - 1,
                );
              });
            }}
            color="primary"
          >
            Save now
          </Button>
        </Message>
      ) : null}
      <SegmentContainer>
        <Card fluid style={{ zIndex: "auto" }}>
          <CardContent>
            <Grid mobile={1} columns={2}>
              <GridColumn span={2} computer={1}>
                <H3>
                  {t("admin.settings.branding.logo.title")}
                  <Subheader>
                    {t("admin.settings.branding.logo.info", { user })}
                  </Subheader>
                </H3>
                <FileChooser
                  forType="organisation"
                  forId={organisation._id}
                  trigger={
                    <Button color="primary">
                      {t("admin.settings.branding.logo.button")}
                    </Button>
                  }
                  onComplete={saveLogo}
                />
              </GridColumn>
              <GridColumn span={2} computer={1}>
                <img
                  alt="Organisation logo"
                  src={organisation.darkLogoUrl}
                  style={{
                    maxWidth: "100%",
                    display: "block",
                    margin: "10px auto",
                  }}
                />
              </GridColumn>
            </Grid>
          </CardContent>
        </Card>

        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.colourScheme.title")}
              <Subheader>
                {t("admin.settings.branding.colourScheme.info")}
              </Subheader>
            </H3>
            <Grid mobile={1} columns={2} style={{ marginTop: 10 }}>
              <GridColumn style={{ textAlign: "center" }}>
                <H4>{t("admin.settings.branding.colourScheme.primary")}</H4>
                <div
                  style={{
                    width: "220px",
                    display: "block",
                    margin: "10px auto",
                  }}
                >
                  <ColourChooser
                    value={organisation.primaryColour ?? ""}
                    onChange={(c) => setColour("primaryColour", c)}
                  />
                </div>
              </GridColumn>
              <GridColumn style={{ textAlign: "center" }}>
                <H4>{t("admin.settings.branding.colourScheme.secondary")}</H4>
                <div
                  style={{
                    width: "220px",
                    display: "block",
                    margin: "10px auto",
                  }}
                >
                  <ColourChooser
                    value={organisation.secondaryColour ?? ""}
                    onChange={(c) => setColour("secondaryColour", c)}
                  />
                </div>
              </GridColumn>
            </Grid>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <Button onClick={swapColours} color="primary">
                <Icon icon={ShuffleIcon} />
                Swap colours
              </Button>
            </div>
          </CardContent>
        </Card>
      </SegmentContainer>

      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.browserTitle.title")}
              <Subheader>
                {t("admin.settings.branding.browserTitle.info")}
              </Subheader>
            </H3>
            <Input
              style={{ width: "100%" }}
              placeholder={newTitle || util.appName()}
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
              }}
            />
            <div style={{ marginTop: 16 }}>
              <Button
                color="primary"
                disabled={newTitle === user?.ownerOrganisation?.appName}
                onClick={() => {
                  saveOrg("appName", newTitle);
                }}
              >
                Save name
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.favicon.title")}

              <Subheader>{t("admin.settings.branding.favicon.info")}</Subheader>
            </H3>
            <img
              alt="Favicon"
              src={getImageUrl(
                user.ownerOrganisation,
                "favicon",
                ["256x256"],
                "images/icon.png",
              )}
              style={{ maxWidth: "25%", display: "block", margin: "10px auto" }}
            />
            <FileChooser
              forType="organisation"
              forId={organisation._id}
              trigger={
                <Button color="primary">
                  {t("admin.settings.branding.logo.button")}
                </Button>
              }
              onComplete={saveFavicon}
            />
            {organisation.faviconUrl && (
              <Button
                onClick={() => {
                  saveFavicon("");
                }}
                variant="outline"
              >
                Remove
              </Button>
            )}
          </CardContent>
        </Card>
      </SegmentContainer>

      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.welcomeText.title")}
              <Subheader>
                {t("admin.settings.branding.welcomeText.info")}
              </Subheader>
            </H3>
            <TextArea
              placeholder={t("admin.settings.branding.welcomeText.placeholder")}
              value={newWelcomeText}
              onChange={(e) => {
                setNewWelcomeText(e.target.value);
              }}
            />
            <Button
              disabled={organisation.welcomeText === newWelcomeText}
              onClick={() => {
                saveOrg("welcomeText", newWelcomeText);
              }}
              style={{ marginTop: 16 }}
              color="primary"
            >
              {t("generic.save")}
            </Button>
          </CardContent>
        </Card>

        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.homepageText.title")}
              <Subheader>
                {t("admin.settings.branding.homepageText.info")}
              </Subheader>
            </H3>
            <TextArea
              placeholder={t(
                "admin.settings.branding.homepageText.placeholder",
              )}
              value={newDashboardText}
              onChange={(e) => {
                setNewDashboardText(e.target.value);
              }}
            />
            <Button
              disabled={organisation.dashboardText === newDashboardText}
              onClick={() => {
                saveOrg("dashboardText", newDashboardText);
              }}
              style={{ marginTop: 16 }}
              color="primary"
            >
              {t("generic.save")}
            </Button>
          </CardContent>
        </Card>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.homepageTasklist.title")}
              <Subheader>
                {t("admin.settings.branding.homepageTasklist.info")}
              </Subheader>
            </H3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {(
                [
                  "completeProfile",
                  "exploreChallenges",
                  "createIdea",
                  "submitIdea",
                  "joinGroup",
                  "setup2fa",
                ] as const
              ).map((key) => (
                <Checkbox
                  key={key}
                  toggle
                  checked={homepageTasklist[key]}
                  onChange={(_e, { checked }) => {
                    const next = { ...homepageTasklist, [key]: !!checked };
                    setHomepageTasklist(next);
                    saveOrg("homepageTasklist", next);
                  }}
                >
                  {t(`admin.settings.branding.homepageTasklist.${key}`)}
                </Checkbox>
              ))}
            </div>
          </CardContent>
        </Card>
      </SegmentContainer>
      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.branding.terminology.title")}
              <Subheader>
                {t("admin.settings.branding.terminology.info")}
              </Subheader>
            </H3>

            <FormField>
              <FormLabel>
                {t("admin.settings.branding.terminology.challenge")}
              </FormLabel>
              <CapitalisedInput
                fluid
                placeholder="Challenge"
                value={newTerminology?.en?.challenge}
                onChange={(_e, { value }) => {
                  setNewTerminology((prev) => {
                    // map for each language and update
                    return languages.reduce<OrganisationTerminology>(
                      (acc, lang) => {
                        acc[lang.key] = {
                          ...(prev[lang.key] ?? {}),
                          challenge: value.toLowerCase(),
                          challenges: value
                            ? t("generic.challengesDefault", {
                                lng: "en",
                                "generic.challenge": value.toLowerCase(),
                              })
                            : "",
                        };
                        return acc;
                      },
                      {},
                    );
                  });
                }}
              />
            </FormField>
            <Accordion fluid styled>
              <AccordionTitle
                active={terminologyTranslationOpen?.challenge}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    challenge: !prev.challenge,
                  }))
                }
              >
                {t("admin.settings.branding.terminology.translations")}
              </AccordionTitle>
              <AccordionContent active={terminologyTranslationOpen?.challenge}>
                {languages.map((lang) => (
                  <>
                    <Grid key={lang.key} columns={2} mobile={1}>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{lang.name}</FormLabel>
                          <CapitalisedInput
                            fluid
                            placeholder={`Default: ${t("generic.challengeDefault", { lng: lang.key })}`}
                            value={newTerminology?.[lang.key]?.challenge}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  challenge: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{`${lang.name} (Plural)`}</FormLabel>
                          <CapitalisedInput
                            fluid
                            disabled={
                              !newTerminology?.[lang.key]?.challenge &&
                              !newTerminology?.[lang.key]?.challenges
                            }
                            placeholder={`Default: ${t("generic.challengesDefault", { lng: lang.key, "generic.challenge": newTerminology?.[lang.key]?.challenge || "challenge" })}`}
                            value={newTerminology?.[lang.key]?.challenges}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  challenges: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <Divider hidden />
                  </>
                ))}
              </AccordionContent>
            </Accordion>
            <Divider />
            <FormField>
              <FormLabel>
                {t("admin.settings.branding.terminology.idea")}
              </FormLabel>
              <CapitalisedInput
                fluid
                placeholder={`${t("common:capitalise", { key: "generic.idea" })}`}
                value={newTerminology?.en?.idea}
                onChange={(_e, { value }) => {
                  setNewTerminology((prev) => {
                    // map for each language and update
                    return languages.reduce<OrganisationTerminology>(
                      (acc, lang) => {
                        acc[lang.key] = {
                          ...(prev[lang.key] ?? {}),
                          idea: value.toLowerCase(),
                          ideas: value
                            ? t("generic.ideasDefault", {
                                lng: "en",
                                "generic.idea": value.toLowerCase(),
                              })
                            : "",
                        };
                        return acc;
                      },
                      {},
                    );
                  });
                }}
              />
            </FormField>
            <Accordion fluid styled>
              <AccordionTitle
                active={terminologyTranslationOpen?.idea}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    idea: !prev.idea,
                  }))
                }
              >
                {t("admin.settings.branding.terminology.translations")}
              </AccordionTitle>
              <AccordionContent active={terminologyTranslationOpen?.idea}>
                {languages.map((lang) => (
                  <>
                    <Grid key={lang.key} columns={2} mobile={1}>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{lang.name}</FormLabel>
                          <CapitalisedInput
                            fluid
                            placeholder={`Default: ${t("generic.ideaDefault", { lng: lang.key })}`}
                            value={newTerminology?.[lang.key]?.idea}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  idea: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{`${lang.name} (Plural)`}</FormLabel>
                          <CapitalisedInput
                            fluid
                            disabled={!newTerminology?.[lang.key]?.ideas}
                            placeholder={`Default: ${t("generic.ideas", { lng: lang.key, "generic.idea": newTerminology?.[lang.key]?.idea || "idea" })}`}
                            value={newTerminology?.[lang.key]?.ideas}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  ideas: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <Divider hidden />
                  </>
                ))}
              </AccordionContent>
            </Accordion>
            <Divider />
            <FormField>
              <FormLabel>Rename &apos;Impact&apos; to:</FormLabel>
              <CapitalisedInput
                fluid
                placeholder={`"${t("common:capitalise", { key: "generic.impact" })}"`}
                value={newTerminology?.en?.impact}
                onChange={(_e, { value }) => {
                  setNewTerminology((prev) => {
                    // map for each language and update
                    return languages.reduce<OrganisationTerminology>(
                      (acc, lang) => {
                        acc[lang.key] = {
                          ...(prev[lang.key] ?? {}),
                          impact: value.toLowerCase(),
                          impacts: value
                            ? t("generic.impactsDefault", {
                                lng: "en",
                                "generic.impact": value.toLowerCase(),
                              })
                            : "",
                        };
                        return acc;
                      },
                      {},
                    );
                  });
                }}
              />
            </FormField>
            <Accordion fluid styled>
              <AccordionTitle
                active={terminologyTranslationOpen?.impact}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    impact: !prev.impact,
                  }))
                }
              >
                {t("admin.settings.branding.terminology.translations")}
              </AccordionTitle>
              <AccordionContent active={terminologyTranslationOpen?.impact}>
                {languages.map((lang) => (
                  <>
                    <Grid key={lang.key} columns={2} mobile={1}>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{lang.name}</FormLabel>
                          <CapitalisedInput
                            fluid
                            placeholder={`Default: ${t("generic.impactDefault", { lng: lang.key })}`}
                            value={newTerminology?.[lang.key]?.impact}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  impact: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{`${lang.name} (Plural)`}</FormLabel>
                          <CapitalisedInput
                            fluid
                            disabled={!newTerminology?.[lang.key]?.impacts}
                            placeholder={`Default: ${t("generic.impacts", { lng: lang.key, "generic.idea": newTerminology?.[lang.key]?.idea || "idea" })}`}
                            value={newTerminology?.[lang.key]?.impacts}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  impacts: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <Divider hidden />
                  </>
                ))}
              </AccordionContent>
            </Accordion>
            <Divider />
            <FormField>
              <FormLabel>
                {t("admin.settings.branding.terminology.group")}
              </FormLabel>
              <CapitalisedInput
                fluid
                placeholder="Group"
                value={newTerminology?.en?.group}
                onChange={(_e, { value }) => {
                  setNewTerminology((prev) => {
                    return languages.reduce<OrganisationTerminology>(
                      (acc, lang) => {
                        acc[lang.key] = {
                          ...(prev[lang.key] ?? {}),
                          group: value.toLowerCase(),
                          groups: value
                            ? t("generic.groupsDefault", {
                                lng: "en",
                                "generic.group": value.toLowerCase(),
                              })
                            : "",
                        };
                        return acc;
                      },
                      {},
                    );
                  });
                }}
              />
            </FormField>
            <Accordion fluid styled>
              <AccordionTitle
                active={terminologyTranslationOpen?.group}
                onClick={() =>
                  setTerminologyTranslationOpen((prev) => ({
                    ...prev,
                    group: !prev.group,
                  }))
                }
              >
                {t("admin.settings.branding.terminology.translations")}
              </AccordionTitle>
              <AccordionContent active={terminologyTranslationOpen?.group}>
                {languages.map((lang) => (
                  <>
                    <Grid key={lang.key} columns={2} mobile={1}>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{lang.name}</FormLabel>
                          <CapitalisedInput
                            fluid
                            placeholder={`Default: ${t("generic.groupDefault", { lng: lang.key })}`}
                            value={newTerminology?.[lang.key]?.group}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  group: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>{`${lang.name} (Plural)`}</FormLabel>
                          <CapitalisedInput
                            fluid
                            disabled={
                              !newTerminology?.[lang.key]?.group &&
                              !newTerminology?.[lang.key]?.groups
                            }
                            placeholder={`Default: ${t("generic.groupsDefault", { lng: lang.key, "generic.group": newTerminology?.[lang.key]?.group || "group" })}`}
                            value={newTerminology?.[lang.key]?.groups}
                            onChange={(_e, { value }) => {
                              setNewTerminology((prev) => ({
                                ...prev,
                                [lang.key]: {
                                  ...(prev[lang.key] ?? {}),
                                  groups: value.toLowerCase(),
                                },
                              }));
                            }}
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <Divider hidden />
                  </>
                ))}
              </AccordionContent>
            </Accordion>
            <Button
              style={{ marginTop: "1rem" }}
              onClick={() => {
                saveOrg("terminology", newTerminology);
                setInterpolationDictionary(newTerminology);
              }}
              color="primary"
            >
              {t("generic.save")}
            </Button>
          </CardContent>
        </Card>
        <Card fluid style={{ zIndex: "auto" }}>
          <CardContent>
            <Grid mobile={1}>
              <GridColumn>
                <H3>
                  Navigation Bar
                  <Subheader>
                    You can change the order of the navigation bar items by
                    dragging them. The first entry in this list will be the
                    first page users see when loading into the platform.
                  </Subheader>
                </H3>
                <Message size="small" color="blue">
                  The top five visible entries will be displayed directly on the
                  navigation bar, remaining visible entires will be accessible
                  via the &apos;More&apos; dropdown.
                </Message>
                <div>
                  <Button
                    onClick={() => {
                      saveOrg("navbar", toDatabase(visibleEntries));
                      setNavBarUnsaved(false);
                    }}
                    disabled={!navBarUnsaved}
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      saveOrg("navbar", null);
                      setNavBarUnsaved(false);
                    }}
                  >
                    <Icon icon={Redo2Icon} />
                    Reset
                  </Button>
                </div>
                <div style={{ clear: "both" }} />
                <Divider />
                <DndContext
                  onDragOver={(event, _manager, setMoveResult) => {
                    const { source, target } = event.operation;
                    if (!target || !source) return;

                    if (
                      target.id === "disabled-navbar" ||
                      ("group" in target && target.group === "disabled-navbar")
                    ) {
                      const item = visibleEntries.find(
                        (item) => item.id === source.id,
                      );
                      if (item?.permissions) {
                        event.preventDefault();
                        setMoveResult(makeResult(event, false));
                        return;
                      }
                    }
                    const result = move(
                      {
                        "enabled-navbar": visibleEntries,
                        "disabled-navbar": hiddenEntries,
                      },
                      event,
                    );
                    setHiddenEntries(result["disabled-navbar"]);
                    setVisibleEntries(result["enabled-navbar"]);
                    setNavBarUnsaved(true);
                  }}
                  onDidMove={(moveResult) => {
                    if (
                      !moveResult.didMove &&
                      (moveResult.targetId === "disabled-navbar" ||
                        moveResult.targetGroup === "disabled-navbar")
                    ) {
                      toast.error("You cannot hide this entry");
                    }
                  }}
                >
                  <H4>Visible entries</H4>
                  <Sortable index={0} disabled id="enabled-navbar">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {visibleEntries.map((item, index) => (
                        <Sortable key={item.id} id={item.id} index={index}>
                          <NavBarEntry
                            setEditingLink={setEditingLink}
                            setEditingLinkModalOpen={setEditingLinkModalOpen}
                            deleteCustomLink={deleteCustomLink}
                            key={item.id}
                            item={item}
                          />
                        </Sortable>
                      ))}
                    </div>
                  </Sortable>
                  <H4>Hidden entries</H4>
                  <Sortable index={0} disabled id="disabled-navbar">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {hiddenEntries.length === 0 && (
                        <Message>There are no hidden entries</Message>
                      )}
                      {hiddenEntries.map((item, index) => (
                        <Sortable key={item.id} id={item.id} index={index}>
                          <NavBarEntry
                            setEditingLink={setEditingLink}
                            setEditingLinkModalOpen={setEditingLinkModalOpen}
                            deleteCustomLink={deleteCustomLink}
                            key={item.id}
                            item={item}
                          />
                        </Sortable>
                      ))}
                    </div>
                  </Sortable>
                </DndContext>
                <Divider />
                <H4>
                  Custom links
                  <Subheader>
                    Add custom links to the end of the navigation bar, for
                    example to link to an external blog or website.
                  </Subheader>
                </H4>
                <Button
                  onClick={() => setEditingLinkModalOpen(true)}
                  size="small"
                >
                  <Icon icon={PlusIcon} />
                  Add custom navigation bar link
                </Button>
              </GridColumn>
            </Grid>
          </CardContent>
        </Card>
      </SegmentContainer>
      {(organisation.enabledFeatures?.includes("customOrganisationFont") ||
        util.organisationFeaturesEnabled(user, [
          "customOrganisationFont" as NonNullable<
            SettingsOwnerOrganisation["enabledFeatures"]
          >[number],
        ])) && (
        <SegmentContainer>
          <Card fluid>
            <CardContent>
              <H3>
                {t("admin.settings.branding.fontFamily.title")}
                <Subheader>
                  {t("admin.settings.branding.fontFamily.info")}
                </Subheader>
              </H3>
              <Grid columns={2} mobile={1}>
                <GridColumn>
                  <FormField>
                    <FormLabel>H1 font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.h1 || "Inter"}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, h1: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
                <GridColumn>
                  <FormField>
                    <FormLabel>H2 font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.h2}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, h2: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
              </Grid>
              <Grid columns={2} mobile={1}>
                <GridColumn>
                  <FormField>
                    <FormLabel>H3 font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.h3 || "Inter"}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, h3: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
                <GridColumn>
                  <FormField>
                    <FormLabel>H4 font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.h4 || "Inter"}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, h4: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
              </Grid>
              <Grid columns={2} mobile={1}>
                <GridColumn>
                  <FormField>
                    <FormLabel>H5 font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.h5 || "Inter"}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, h5: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
                <GridColumn>
                  <FormField>
                    <FormLabel>Body font</FormLabel>
                    <Dropdown
                      variant="outline"
                      search
                      options={constants.googleFonts}
                      value={fontFamily.body || "Inter"}
                      onChange={(_e, { value }) => {
                        setFontFamily({ ...fontFamily, body: value as string });
                        loadGoogleFont(value as string, [400, 500, 600, 700]);
                      }}
                    />
                  </FormField>
                </GridColumn>
              </Grid>
              <div style={{ marginTop: 16 }}>
                <Button
                  onClick={() => saveOrg("fontFamily", fontFamily)}
                  color="primary"
                >
                  {t("generic.save")}
                </Button>
                <Button
                  onClick={() => {
                    util
                      .confirm(
                        "Reset all fonts",
                        "Are you sure you want to reset all fonts to the default?",
                      )
                      .then(() => {
                        setFontFamily(defaultFontFamily);
                        saveOrg("fontFamily", defaultFontFamily);
                      })
                      .catch(() => {});
                  }}
                >
                  <Icon icon={Redo2Icon} />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card fluid>
            <CardContent>
              <H3>
                Font preview
                <Subheader>Preview of your selected fonts.</Subheader>
              </H3>
              <H1 style={{ fontFamily: fontFamily.h1 }}>
                Example text in H1 font
              </H1>
              <H2 style={{ fontFamily: fontFamily.h2 }}>
                Example text in H2 font
              </H2>
              <H3 style={{ fontFamily: fontFamily.h3 }}>
                Example text in H3 font
              </H3>
              <H4 style={{ fontFamily: fontFamily.h4 }}>
                Example text in H4 font
              </H4>
              <H5 style={{ fontFamily: fontFamily.h5 }}>
                Example text in H5 font
              </H5>
              <p style={{ fontFamily: fontFamily.body }}>
                Example text in body font
              </p>
            </CardContent>
          </Card>
        </SegmentContainer>
      )}
      <Modal
        open={editingLinkModalOpen}
        onClose={() => setEditingLinkModalOpen(false)}
      >
        <ModalHeader>Custom navigation bar link</ModalHeader>
        <ModalContent>
          <ModalDescription>
            <p>
              Add a navigation bar link to direct users to additional content.
            </p>
            <p>
              <strong>Note:</strong> This link will not be visible to members of
              your organisation until you add it to the navigation bar.
            </p>
            <FormField>
              <FormLabel>Title</FormLabel>
              <Input
                type="text"
                placeholder="My custom link"
                value={editingLink?.title}
                onChange={(e) =>
                  setEditingLink({ ...editingLink, title: e.target.value })
                }
              />
            </FormField>
            <FormField>
              <FormLabel>Link</FormLabel>
              <Input
                type="text"
                placeholder="https://example.com"
                value={editingLink?.location}
                onChange={(e) =>
                  setEditingLink({ ...editingLink, location: e.target.value })
                }
              />
            </FormField>
            <Checkbox
              style={{ marginTop: 10 }}
              checked={editingLink?.external}
              onChange={(_e, { checked }) =>
                setEditingLink({ ...editingLink, external: checked })
              }
            >
              Open link in new tab
            </Checkbox>
          </ModalDescription>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setEditingLinkModalOpen(false);
              setEditingLink(emptyOrgNavLink());
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              updateOrgLinks();
              setEditingLink(emptyOrgNavLink());
            }}
            // disabled={!editingLink?.location || !editingLink?.title}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Save
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
};

export default SettingsBranding;
